import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Plus, Trash2, Pencil, X, Upload, CalendarDays, ChevronRight, Download } from "lucide-react";
import ColumnFilter from "@/components/ColumnFilter";
import { useAuth } from "@/context/AuthContext";

const num = (v) => (v === "" || v == null ? 0 : parseFloat(String(v).replace(",", ".")) || 0);
const dmy = (iso) => { if (!iso) return "-"; const p = String(iso).slice(0, 10).split("-"); return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : iso; };

export default function IlDisiHarc() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("ana");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hareketOpen, setHareketOpen] = useState(false);
  const [anaEdit, setAnaEdit] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [detay, setDetay] = useState(null);
  const [colSel, setColSel] = useState({});
  const [sort, setSort] = useState({ key: null, dir: null });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/il-disi-harc").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id, borclu) => {
    if (!window.confirm(`${borclu} dosyası ve tüm hareketleri silinsin mi?`)) return;
    try { await api.delete(`/il-disi-harc/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  const ACOLS = [
    { key: "borclu", label: "Borçlu", type: "text" },
    { key: "dosya_no", label: "Dosya No", type: "text" },
    { key: "takip_acilis_masrafi", label: "Takip Açılış", type: "money" },
    { key: "harc_toplam", label: "Harç", type: "money" },
    { key: "toplam_masraf", label: "Toplam Masraf", type: "money" },
    { key: "gayri_resmi_masraf", label: "Gayri Resmi", type: "money" },
    { key: "resmi_masraf_total", label: "Resmi Masraf Total", type: "money" },
  ];
  const trLower = (s) => String(s ?? "").toLocaleLowerCase("tr");
  const disp = (c, r) => (c.type === "money" ? fmt(r[c.key]) : String(r[c.key] ?? ""));
  const distinct = (c) => {
    const arr = [...new Set(rows.map((r) => disp(c, r)))];
    if (c.type === "money") arr.sort((a, b) => (parseFloat(String(a).replace(/[^\d,-]/g, "").replace(",", ".")) || 0) - (parseFloat(String(b).replace(/[^\d,-]/g, "").replace(",", ".")) || 0));
    else arr.sort((a, b) => trLower(a).localeCompare(trLower(b), "tr"));
    return arr;
  };
  const shown = rows.filter((r) => ACOLS.every((c) => { const sel = colSel[c.key]; return !sel || sel.includes(disp(c, r)); }));
  let view = shown;
  if (sort.key) {
    const col = ACOLS.find((c) => c.key === sort.key);
    view = [...shown].sort((a, b) => {
      let av, bv;
      if (col.type === "money") { av = Number(a[sort.key]) || 0; bv = Number(b[sort.key]) || 0; }
      else { av = trLower(disp(col, a)); bv = trLower(disp(col, b)); }
      return av < bv ? (sort.dir === "asc" ? -1 : 1) : av > bv ? (sort.dir === "asc" ? 1 : -1) : 0;
    });
  }
  const tot = view.reduce((a, r) => ({
    e: a.e + (r.takip_acilis_masrafi || 0), harc: a.harc + (r.harc_toplam || 0),
    h: a.h + (r.toplam_masraf || 0), n: a.n + (r.gayri_resmi_masraf || 0), l: a.l + (r.resmi_masraf_total || 0),
  }), { e: 0, harc: 0, h: 0, n: 0, l: 0 });

  return (
    <Layout>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <div className="kicker mb-1">İL DIŞI</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">İl Dışı Harç &amp; Masraf</h1>
          <p className="text-sm text-[#8C8377]">Dosyaya tıklayıp tarihli masraf ekleyebilir, haftalık (Cumartesi–Cuma) masraf raporu alabilirsiniz.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => setImportOpen(true)} data-testid="ildisi-import" className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#E7DECC] text-[#8C8377] rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"><Upload className="w-4 h-4" /> Excel İçe Aktar</button>
          <button onClick={() => setAnaEdit({})} data-testid="ildisi-yeni-dosya" className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#B8933F] text-[#B8933F] rounded-sm text-sm font-medium hover:bg-[#B8933F]/5 transition-colors duration-150"><Plus className="w-4 h-4" /> Yeni Dosya</button>
          <button onClick={() => setHareketOpen(true)} data-testid="ildisi-yeni-masraf" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150"><Plus className="w-4 h-4" /> Masraf Ekle</button>
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b border-[#E7DECC] overflow-x-auto no-scrollbar">
        <button onClick={() => setTab("ana")} data-testid="tab-ana" className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 whitespace-nowrap shrink-0 transition-colors ${tab === "ana" ? "border-[#B8933F] text-[#B8933F]" : "border-transparent text-[#8C8377] hover:text-[#26231E]"}`}>Ana Tablo</button>
        <button onClick={() => setTab("rapor")} data-testid="tab-rapor" className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 whitespace-nowrap shrink-0 transition-colors inline-flex items-center gap-1.5 ${tab === "rapor" ? "border-[#B8933F] text-[#B8933F]" : "border-transparent text-[#8C8377] hover:text-[#26231E]"}`}><CalendarDays className="w-4 h-4" /> Haftalık Rapor</button>
      </div>

      {tab === "ana" ? (
        <div className="bg-white border border-[#E7DECC] rounded-sm overflow-x-auto" data-testid="ildisi-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
                {ACOLS.map((c) => (
                  <th key={c.key} className={`px-3 py-2.5 kicker whitespace-nowrap ${c.type === "money" ? "text-right" : "text-left"}`}>
                    <div className={`flex items-center gap-1 ${c.type === "money" ? "justify-end" : "justify-between"}`}>
                      <span>{c.label}{sort.key === c.key ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}</span>
                      <ColumnFilter col={c} values={distinct(c)} selected={colSel[c.key] ?? null} onChange={(v) => setColSel((p) => ({ ...p, [c.key]: v }))} sortDir={sort.key === c.key ? sort.dir : null} onSort={(dir) => setSort({ key: c.key, dir })} onClearSort={() => setSort((s) => (s.key === c.key ? { key: null, dir: null } : s))} />
                    </div>
                  </th>
                ))}
                <th className="px-3 py-2.5 kicker text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-[#8C8377]">Yükleniyor...</td></tr>
              ) : view.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-[#8C8377]" data-testid="ildisi-empty">Kayıt yok. "Yeni Dosya" ile başlayın.</td></tr>
              ) : view.map((r) => (
                <tr key={r.id} onClick={() => setDetay(r)} className="border-b border-[#E7DECC] last:border-0 cursor-pointer hover:bg-[#F3ECDD]" data-testid={`ildisi-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium"><span className="inline-flex items-center gap-1">{r.borclu}<ChevronRight className="w-3.5 h-3.5 text-[#B3A88F]" /></span></td>
                  <td className="px-3 py-2 text-[#8C8377]">{r.dosya_no || "-"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(r.takip_acilis_masrafi)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(r.harc_toplam)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(r.toplam_masraf)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#B8933F]">{fmt(r.gayri_resmi_masraf)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#B8933F]">{fmt(r.resmi_masraf_total)}</td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setAnaEdit(r)} data-testid={`ildisi-edit-${r.id}`} className="p-1.5 text-[#8C8377] hover:text-[#B8933F]"><Pencil className="w-4 h-4" /></button>
                      {isAdmin && <button onClick={() => del(r.id, r.borclu)} data-testid={`ildisi-del-${r.id}`} className="p-1.5 text-[#8C8377] hover:text-[#A63A3A]"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {view.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[#E7DECC] font-bold bg-[#F3ECDD]">
                  <td className="px-3 py-2.5" colSpan={2}>TOPLAM</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmt(tot.e)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmt(tot.harc)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmt(tot.h)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[#B8933F]">{fmt(tot.n)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[#B8933F]">{fmt(tot.l)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : (
        <WeeklyReport />
      )}

      {hareketOpen && <HareketDialog onClose={() => setHareketOpen(false)} onSaved={() => { setHareketOpen(false); load(); }} />}
      {anaEdit && <AnaDialog rec={anaEdit} onClose={() => setAnaEdit(null)} onSaved={() => { setAnaEdit(null); load(); }} />}
      {importOpen && <ImportDialog onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />}
      {detay && <DosyaDetayModal rec={detay} onClose={() => setDetay(null)} onChanged={load} />}
    </Layout>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="kicker block mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F]" />
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white rounded-sm border border-[#E7DECC] w-full ${wide ? "max-w-2xl" : "max-w-lg"} shadow-2xl max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7DECC] sticky top-0 bg-white z-10">
          <span className="font-heading font-bold">{title}</span>
          <button onClick={onClose} className="p-1 text-[#8C8377] hover:text-[#26231E]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// Ortak "Masraf Ekle" alt formu — tarih, tutar, açıklama, tür (Masraf / Takip Açılış)
function MasrafForm({ onSubmit, saving }) {
  const [tarih, setTarih] = useState(new Date().toISOString().slice(0, 10));
  const [tutar, setTutar] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [tur, setTur] = useState("masraf"); // masraf | takip
  const [kategori, setKategori] = useState("");
  const [kats, setKats] = useState([]);

  useEffect(() => { api.get("/il-disi-kategori").then((r) => setKats(r.data || [])).catch(() => {}); }, []);

  const submit = () => {
    const t = num(tutar);
    if (!t) { toast.error("Tutar girin"); return; }
    onSubmit({ tarih, tutar: t, aciklama, tur, kategori }, () => { setTutar(""); setAciklama(""); setKategori(""); });
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-end" data-testid="masraf-form">
      <div className="sm:col-span-1"><label className="kicker block mb-1">TARİH</label><input type="date" value={tarih} onChange={(e) => setTarih(e.target.value)} data-testid="masraf-tarih" className="w-full px-2 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F]" /></div>
      <div className="sm:col-span-1"><label className="kicker block mb-1">TUTAR</label><input type="number" value={tutar} onChange={(e) => setTutar(e.target.value)} data-testid="masraf-tutar" className="w-full px-2 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F]" /></div>
      <div className="sm:col-span-1"><label className="kicker block mb-1">TÜR</label><select value={tur} onChange={(e) => setTur(e.target.value)} data-testid="masraf-tur" className="w-full px-2 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F] bg-white"><option value="masraf">Masraf</option><option value="takip">Takip Açılış</option></select></div>
      <div className="sm:col-span-1"><label className="kicker block mb-1">KATEGORİ</label><select value={kategori} onChange={(e) => setKategori(e.target.value)} data-testid="masraf-kategori" className="w-full px-2 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F] bg-white"><option value="">(Seçiniz)</option>{kats.map((k) => <option key={k.id} value={k.ad}>{k.ad}</option>)}</select></div>
      <div className="sm:col-span-1"><label className="kicker block mb-1">AÇIKLAMA</label><input value={aciklama} onChange={(e) => setAciklama(e.target.value)} data-testid="masraf-aciklama" className="w-full px-2 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F]" /></div>
      <div className="sm:col-span-1"><button onClick={submit} disabled={saving} data-testid="masraf-ekle-btn" className="w-full px-3 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium disabled:opacity-60">{saving ? "..." : "Ekle"}</button></div>
    </div>
  );
}

function DosyaDetayModal({ rec, onClose, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [hareketler, setHareketler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ana, setAna] = useState(rec);

  const loadH = useCallback(() => {
    setLoading(true);
    api.get(`/il-disi-harc/hareketler?ana_id=${rec.id}`).then((r) => setHareketler(r.data || [])).finally(() => setLoading(false));
  }, [rec.id]);
  useEffect(() => { loadH(); }, [loadH]);

  const refreshAna = async () => {
    try { const { data } = await api.get("/il-disi-harc"); const found = (data || []).find((x) => x.id === rec.id); if (found) setAna(found); } catch (e) { console.error("İl Dışı Harç ana kaydı yenilenemedi:", e); }
    onChanged && onChanged();
  };

  const add = async ({ tarih, tutar, aciklama, tur, kategori }, reset) => {
    setSaving(true);
    try {
      const body = { borclu: rec.borclu, dosya_no: rec.dosya_no || "", tarih, aciklama, kategori,
        masraf: tur === "masraf" ? tutar : 0, takip_acilis: tur === "takip" ? tutar : 0 };
      await api.post("/il-disi-harc/hareket", body);
      toast.success("Masraf eklendi");
      reset && reset();
      loadH(); refreshAna();
    } catch (e) { toast.error(e.response?.data?.detail || "Eklenemedi"); } finally { setSaving(false); }
  };

  const delH = async (hid) => {
    if (!window.confirm("Bu masraf hareketi silinsin mi?")) return;
    try { await api.delete(`/il-disi-harc/hareket/${hid}`); toast.success("Silindi"); loadH(); refreshAna(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  const toplam = hareketler.reduce((a, h) => a + (h.masraf || 0) + (h.takip_acilis || 0), 0);

  return (
    <Modal title={`${ana.borclu} — ${ana.dosya_no || "Dosya"}`} onClose={onClose} wide>
      <div className="space-y-4" data-testid="ildisi-detay">
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div className="p-2 rounded-sm bg-[#F3ECDD]"><div className="kicker">Takip Açılış</div><div className="tabular-nums font-semibold">{fmt(ana.takip_acilis_masrafi)}</div></div>
          <div className="p-2 rounded-sm bg-[#F3ECDD]"><div className="kicker">Toplam Masraf</div><div className="tabular-nums font-semibold">{fmt(ana.toplam_masraf)}</div></div>
          <div className="p-2 rounded-sm bg-[#B8933F]/5"><div className="kicker">Resmi Masraf Total</div><div className="tabular-nums font-semibold text-[#B8933F]">{fmt(ana.resmi_masraf_total)}</div></div>
        </div>

        <div className="border border-[#E7DECC] rounded-sm p-3 bg-[#FAFAFA]">
          <div className="kicker mb-2">Masraf Ekle</div>
          <MasrafForm onSubmit={add} saving={saving} />
        </div>

        <div>
          <div className="kicker mb-2">Masraf Hareketleri ({hareketler.length})</div>
          <div className="border border-[#E7DECC] rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#F3ECDD] border-b border-[#E7DECC]">
                <th className="px-3 py-2 kicker text-left">Tarih</th>
                <th className="px-3 py-2 kicker text-left">Tür</th>
                <th className="px-3 py-2 kicker text-left">Masraf Açıklamaları</th>
                <th className="px-3 py-2 kicker text-left">Açıklama</th>
                <th className="px-3 py-2 kicker text-left">İşleyen</th>
                <th className="px-3 py-2 kicker text-right">Tutar</th>
                <th className="px-3 py-2"></th>
              </tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-[#8C8377]">Yükleniyor...</td></tr>
                ) : hareketler.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-[#8C8377]" data-testid="detay-empty">Henüz masraf yok.</td></tr>
                ) : hareketler.map((h) => (
                  <tr key={h.id} className="border-b border-[#E7DECC] last:border-0" data-testid={`detay-hareket-${h.id}`}>
                    <td className="px-3 py-2">{dmy(h.tarih)}</td>
                    <td className="px-3 py-2">{(h.takip_acilis || 0) > 0 ? "Takip Açılış" : "Masraf"}</td>
                    <td className="px-3 py-2">{h.kategori ? <span className={h.gayri_resmi ? "text-[#B8933F] font-medium" : ""}>{h.kategori}</span> : "-"}</td>
                    <td className="px-3 py-2 text-[#8C8377]">{h.aciklama || "-"}</td>
                    <td className="px-3 py-2 text-[#8C8377]">{h.isleyen_ad || h.isleyen_email || "-"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt((h.masraf || 0) + (h.takip_acilis || 0))}</td>
                    <td className="px-3 py-2 text-right">{isAdmin && <button onClick={() => delH(h.id)} data-testid={`detay-del-${h.id}`} className="p-1 text-[#8C8377] hover:text-[#A63A3A]"><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
              {hareketler.length > 0 && (
                <tfoot><tr className="bg-[#F3ECDD] font-bold border-t border-[#E7DECC]"><td className="px-3 py-2" colSpan={5}>TOPLAM</td><td className="px-3 py-2 text-right tabular-nums">{fmt(toplam)}</td><td></td></tr></tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function WeeklyReport() {
  const [weeks, setWeeks] = useState([]);
  const [sel, setSel] = useState("");
  const [detay, setDetay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rColSel, setRColSel] = useState({});
  const [rSort, setRSort] = useState({ key: null, dir: null });

  useEffect(() => {
    api.get("/il-disi-harc/haftalik-ozet").then((r) => {
      const w = r.data || [];
      setWeeks(w);
      if (w.length) setSel(w[0].hafta_baslangic);
    });
  }, []);

  useEffect(() => {
    if (!sel) return;
    setLoading(true);
    api.get(`/il-disi-harc/haftalik-detay?start=${sel}`).then((r) => setDetay(r.data)).finally(() => setLoading(false));
  }, [sel]);

  const dl = async (type) => {
    if (!sel) { toast.error("Önce hafta seçin"); return; }
    try {
      const res = await api.get(`/il-disi-harc/haftalik-detay/export/${type}?start=${sel}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `haftalik_masraf_${sel}.${type === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("İndirilemedi"); }
  };

  const RCOLS = [
    { key: "tarih", label: "Tarih", type: "date" },
    { key: "borclu", label: "Borçlu", type: "text" },
    { key: "dosya_no", label: "Dosya No", type: "text" },
    { key: "kategori", label: "Masraf Açıklamaları", type: "text" },
    { key: "masraf", label: "Masraf", type: "money" },
    { key: "takip_acilis", label: "Takip Açılış", type: "money" },
    { key: "toplam", label: "Toplam", type: "money" },
  ];
  const trLower = (s) => String(s ?? "").toLocaleLowerCase("tr");
  const rdisp = (c, r) => (c.type === "money" ? fmt(r[c.key]) : c.type === "date" ? dmy(r[c.key]) : String(r[c.key] ?? ""));
  const rdistinct = (c) => {
    const arr = [...new Set((detay?.satirlar || []).map((r) => rdisp(c, r)))];
    if (c.type === "money") arr.sort((a, b) => (parseFloat(String(a).replace(/[^\d,-]/g, "").replace(",", ".")) || 0) - (parseFloat(String(b).replace(/[^\d,-]/g, "").replace(",", ".")) || 0));
    else arr.sort((a, b) => trLower(a).localeCompare(trLower(b), "tr"));
    return arr;
  };
  const rfiltered = (detay?.satirlar || []).filter((r) => RCOLS.every((c) => { const sel = rColSel[c.key]; return !sel || sel.includes(rdisp(c, r)); }));
  let rview = rfiltered;
  if (rSort.key) {
    const col = RCOLS.find((c) => c.key === rSort.key);
    rview = [...rfiltered].sort((a, b) => {
      let av, bv;
      if (col.type === "money") { av = Number(a[rSort.key]) || 0; bv = Number(b[rSort.key]) || 0; }
      else if (col.type === "date") { av = String(a[rSort.key] ?? ""); bv = String(b[rSort.key] ?? ""); }
      else { av = trLower(rdisp(col, a)); bv = trLower(rdisp(col, b)); }
      return av < bv ? (rSort.dir === "asc" ? -1 : 1) : av > bv ? (rSort.dir === "asc" ? 1 : -1) : 0;
    });
  }
  const rtot = rview.reduce((s, d) => s + (d.toplam || 0), 0);

  return (
    <div className="space-y-4" data-testid="haftalik-rapor">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="kicker block mb-1">HAFTA (Cumartesi – Cuma)</label>
          <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="hafta-select" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm bg-white focus:outline-none focus:border-[#B8933F] min-w-[280px]">
            {weeks.length === 0 && <option value="">Kayıtlı hafta yok</option>}
            {weeks.map((w) => (
              <option key={w.hafta_baslangic} value={w.hafta_baslangic}>{dmy(w.hafta_baslangic)} – {dmy(w.hafta_bitis)}  ({fmt(w.toplam)})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="kicker block mb-1">TARİHTEN HAFTA BUL</label>
          <input type="date" onChange={(e) => e.target.value && setSel(e.target.value)} data-testid="hafta-tarih" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm bg-white focus:outline-none focus:border-[#B8933F]" />
        </div>
        <div className="flex gap-2 ml-auto">
          <button onClick={() => dl("excel")} disabled={!sel} data-testid="rapor-excel" className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#2F6E4E] text-[#248A3D] rounded-sm text-sm font-medium hover:bg-[#2F6E4E]/5 transition-colors duration-150 disabled:opacity-50"><Download className="w-4 h-4" /> Excel</button>
          <button onClick={() => dl("pdf")} disabled={!sel} data-testid="rapor-pdf" className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#A63A3A] text-[#A63A3A] rounded-sm text-sm font-medium hover:bg-[#A63A3A]/5 transition-colors duration-150 disabled:opacity-50"><Download className="w-4 h-4" /> PDF</button>
        </div>
      </div>

      {detay && (
        <div className="p-3 rounded-sm bg-[#B8933F]/5 flex flex-wrap gap-6 text-sm" data-testid="hafta-ozet">
          <div><span className="text-[#8C8377]">Hafta: </span><b>{dmy(detay.hafta_baslangic)} – {dmy(detay.hafta_bitis)}</b></div>
          <div><span className="text-[#8C8377]">Dosya: </span><b>{detay.dosya_sayisi}</b></div>
          <div><span className="text-[#8C8377]">Haftalık Toplam Masraf: </span><b className="text-[#B8933F] tabular-nums">{fmt(detay.hafta_toplam)}</b></div>
        </div>
      )}

      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
            {RCOLS.map((c) => (
              <th key={c.key} className={`px-3 py-2.5 kicker whitespace-nowrap ${c.type === "money" ? "text-right" : "text-left"}`}>
                <div className={`flex items-center gap-1 ${c.type === "money" ? "justify-end" : "justify-between"}`}>
                  <span>{c.label}{rSort.key === c.key ? (rSort.dir === "asc" ? " ↑" : " ↓") : ""}</span>
                  <ColumnFilter col={c} values={rdistinct(c)} selected={rColSel[c.key] ?? null} onChange={(v) => setRColSel((p) => ({ ...p, [c.key]: v }))} sortDir={rSort.key === c.key ? rSort.dir : null} onSort={(dir) => setRSort({ key: c.key, dir })} onClearSort={() => setRSort((s) => (s.key === c.key ? { key: null, dir: null } : s))} />
                </div>
              </th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-[#8C8377]">Yükleniyor...</td></tr>
            ) : rview.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-[#8C8377]" data-testid="hafta-empty">Bu hafta masraf kaydı yok.</td></tr>
            ) : rview.map((d, i) => (
              <tr key={i} className="border-b border-[#E7DECC] last:border-0" data-testid={`hafta-row-${i}`}>
                <td className="px-3 py-2 tabular-nums whitespace-nowrap">{dmy(d.tarih)}</td>
                <td className="px-3 py-2 font-medium">{d.borclu || "(isimsiz)"}</td>
                <td className="px-3 py-2 text-[#8C8377]">{d.dosya_no || "-"}</td>
                <td className="px-3 py-2 text-[#8C8377]">{d.kategori || d.aciklama || "-"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(d.masraf)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(d.takip_acilis)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#B8933F]">{fmt(d.toplam)}</td>
              </tr>
            ))}
          </tbody>
          {rview.length > 0 && (
            <tfoot><tr className="border-t-2 border-[#E7DECC] font-bold bg-[#F3ECDD]"><td className="px-3 py-2.5" colSpan={6}>HAFTALIK TOPLAM</td><td className="px-3 py-2.5 text-right tabular-nums text-[#B8933F]">{fmt(rtot)}</td></tr></tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function ImportDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (apply) => {
    if (!file) { toast.error("Excel dosyası seçin"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/il-disi-harc/import?apply=${apply}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (apply) {
        toast.success(`İçe aktarıldı: ${data.hareket_eklendi || 0} hareket, ${data.eslesmeyen || 0} eşleşmeyen atlandı, gayri resmi +${fmt(data.gayri_resmi_bucket || 0)}`);
        onDone();
      } else { setPreview(data); }
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); } finally { setBusy(false); }
  };

  return (
    <Modal title="Haftalık Excel İçe Aktar (İl Dışı Harç)" onClose={onClose}>
      <div className="space-y-4" data-testid="ildisi-import-dialog">
        <div className="text-xs text-[#8C8377]">Yalnızca <b>haftalık masraf listeleri</b> okunur; ana tablo değiştirilmez. Tutarlar borçlu/dosya eşleşmesine göre ana tabloya <b>eklenir</b>. Eşleşmeyen satırlar <b>yazılmaz</b>, önizlemede listelenir. İsim/dosya boş satırların toplamı "GAYRİ RESMİ MASRAF" satırına eklenir.</div>
        <input type="file" accept=".xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="ildisi-import-file" className="block w-full text-sm" />
        {preview && (
          <div className="p-3 rounded-sm bg-[#B8933F]/5 text-sm space-y-1" data-testid="ildisi-import-preview">
            <div>Haftalık hareket: <b>{preview.hareket_count}</b> ({preview.hafta_sayisi} hafta)</div>
            <div>Ana tabloda eşleşen (yazılacak): <b className="text-[#2F6E4E]">{preview.matched_count}</b></div>
            <div>Eşleşmeyen (atlanacak): <b className="text-[#B8933F]">{preview.unmatched_count}</b></div>
            <div>Gayri resmi (isim/dosya boş) toplamı: <b className="text-[#B8933F]">{fmt(preview.gayri_resmi_bucket)}</b></div>
            {preview.unmatched_count > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer text-[#B8933F]">Eşleşmeyenleri göster</summary>
                <div className="mt-1 max-h-40 overflow-y-auto text-xs text-[#8C8377] space-y-0.5">
                  {(preview.unmatched || []).map((u, i) => (<div key={i}>{u.borclu || "(isim yok)"} · {u.dosya_no || "-"} · {fmt(u.masraf)}</div>))}
                </div>
              </details>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm">İptal</button>
          {!preview ? (
            <button onClick={() => send(false)} disabled={busy} data-testid="ildisi-import-preview-btn" className="px-5 py-2 bg-[#8C8377] hover:bg-[#4A443B] text-white rounded-sm text-sm font-medium disabled:opacity-60">{busy ? "..." : "Önizle"}</button>
          ) : (
            <button onClick={() => send(true)} disabled={busy} data-testid="ildisi-import-apply-btn" className="px-5 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium disabled:opacity-60">{busy ? "İçe aktarılıyor..." : "Onayla ve İçe Aktar"}</button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function HareketDialog({ onClose, onSaved }) {
  const [borclu, setBorclu] = useState("");
  const [dosya, setDosya] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async ({ tarih, tutar, aciklama, tur, kategori }, reset) => {
    if (!borclu.trim()) { toast.error("Borçlu adı gerekli"); return; }
    setSaving(true);
    try {
      await api.post("/il-disi-harc/hareket", { borclu, dosya_no: dosya, tarih, aciklama, kategori,
        masraf: tur === "masraf" ? tutar : 0, takip_acilis: tur === "takip" ? tutar : 0 });
      toast.success("Masraf eklendi, ana tablo güncellendi");
      reset && reset();
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Eklenemedi"); } finally { setSaving(false); }
  };

  return (
    <Modal title="Masraf Ekle" onClose={onClose} wide>
      <div className="space-y-3" data-testid="ildisi-hareket-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="BORÇLU" value={borclu} onChange={setBorclu} testId="hareket-borclu" />
          <Field label="DOSYA NO (opsiyonel eşleşme)" value={dosya} onChange={setDosya} testId="hareket-dosya" />
        </div>
        <div className="border border-[#E7DECC] rounded-sm p-3 bg-[#FAFAFA]">
          <MasrafForm onSubmit={submit} saving={saving} />
        </div>
        <p className="text-xs text-[#8C8377]">Borçlu/dosya eşleşen dosyaya işlenir. "Takip Açılış" seçilirse ana tabloda Takip Açılış sütununa, aksi halde Toplam Masraf'a eklenir.</p>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm">Kapat</button>
        </div>
      </div>
    </Modal>
  );
}

function AnaDialog({ rec, onClose, onSaved }) {
  const isNew = !rec.id;
  const [f, setF] = useState({
    borclu: rec.borclu || "", dosya_no: rec.dosya_no || "",
    tarih: new Date().toISOString().slice(0, 10),
    takip_acilis_masrafi: rec.takip_acilis_masrafi ?? "", toplam_masraf: rec.toplam_masraf ?? "",
    harc_f: rec.harc_f ?? "", harc_g: rec.harc_g ?? "", harc_i: rec.harc_i ?? "", gayri_resmi_masraf: rec.gayri_resmi_masraf ?? "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const L = num(f.takip_acilis_masrafi) + num(f.toplam_masraf) - Math.min(num(f.gayri_resmi_masraf), num(f.toplam_masraf));

  const submit = async () => {
    if (!f.borclu.trim()) { toast.error("Borçlu adı gerekli"); return; }
    const body = { borclu: f.borclu, dosya_no: f.dosya_no, tarih: f.tarih, takip_acilis_masrafi: num(f.takip_acilis_masrafi), toplam_masraf: num(f.toplam_masraf), harc_f: num(f.harc_f), harc_g: num(f.harc_g), harc_i: num(f.harc_i), gayri_resmi_masraf: num(f.gayri_resmi_masraf) };
    setSaving(true);
    try {
      if (isNew) await api.post("/il-disi-harc", body);
      else await api.put(`/il-disi-harc/${rec.id}`, body);
      toast.success(isNew ? "Dosya eklendi" : "Güncellendi");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); } finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? "Yeni Dosya (Ana Tablo)" : "Dosya Düzenle"} onClose={onClose}>
      <div className="space-y-3" data-testid="ildisi-ana-form">
        <Field label="BORÇLU" value={f.borclu} onChange={(v) => set("borclu", v)} testId="ana-borclu" />
        <Field label="DOSYA NO" value={f.dosya_no} onChange={(v) => set("dosya_no", v)} testId="ana-dosya" />
        {isNew && (
          <div>
            <label className="kicker block mb-1">MASRAF TARİHİ (haftalık rapora işlenir)</label>
            <input type="date" value={f.tarih} onChange={(e) => set("tarih", e.target.value)} data-testid="ana-tarih" className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm text-sm bg-white focus:outline-none focus:border-[#B8933F]" />
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="TAKİP AÇILIŞ MASRAFI" type="number" value={f.takip_acilis_masrafi} onChange={(v) => set("takip_acilis_masrafi", v)} testId="ana-e" />
          <Field label="TOPLAM MASRAF" type="number" value={f.toplam_masraf} onChange={(v) => set("toplam_masraf", v)} testId="ana-h" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="BAŞVURMA HARCI" type="number" value={f.harc_f} onChange={(v) => set("harc_f", v)} testId="ana-harc-f" />
          <Field label="PEŞİN HARÇ" type="number" value={f.harc_g} onChange={(v) => set("harc_g", v)} testId="ana-harc-g" />
          <Field label="İH HARÇLARI" type="number" value={f.harc_i} onChange={(v) => set("harc_i", v)} testId="ana-harc-i" />
        </div>
        <Field label="GAYRİ RESMİ MASRAF" type="number" value={f.gayri_resmi_masraf} onChange={(v) => set("gayri_resmi_masraf", v)} testId="ana-n" />
        <div className="px-3 py-2 rounded-sm bg-[#B8933F]/5 text-sm flex justify-between" data-testid="ana-l-preview">
          <span className="text-[#8C8377]">Resmi Masraf Total</span>
          <b className="text-[#B8933F] tabular-nums">{fmt(L)}</b>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm">İptal</button>
          <button onClick={submit} disabled={saving} data-testid="ana-kaydet" className="px-5 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium disabled:opacity-60">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
        </div>
      </div>
    </Modal>
  );
}
